import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SectionLabel from "@/components/brand/SectionLabel";

/**
 * Shared layout for public /services pages. Handles document head, breadcrumbs, and CTA.
 * Content is passed as children; each page owns its own body copy.
 */
export default function ServicePageLayout({
  title,
  metaDescription,
  h1,
  overline,
  intro,
  canonicalPath,
  jsonLd,
  relatedLinks = [],
  children,
}) {
  useEffect(() => {
    document.title = title;

    // meta description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", metaDescription);

    // canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `https://epicom.ca${canonicalPath}`);

    // og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", `https://epicom.ca${canonicalPath}`);

    // og:title + og:description
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", metaDescription);

    // Per-page JSON-LD Service schema
    let script = document.getElementById("page-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      // On unmount, restore homepage canonical
      if (canon) canon.setAttribute("href", "https://epicom.ca/");
      if (script) script.remove();
    };
  }, [title, metaDescription, canonicalPath, jsonLd]);

  return (
    <div className="min-h-screen bg-bg-primary text-white">
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-4 font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase flex items-center gap-2"
        >
          <Link to="/" className="hover:text-signal transition-colors">Home</Link>
          <ChevronRight size={12} />
          <Link to="/#services" className="hover:text-signal transition-colors">Services</Link>
          <ChevronRight size={12} />
          <span className="text-white">{overline}</span>
        </nav>

        {/* Hero */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 pt-4">
          <SectionLabel className="mb-5">{overline}</SectionLabel>
          <h1 className="heading text-[clamp(2.25rem,7vw,4.5rem)] font-black uppercase text-white leading-[0.95] mb-8 max-w-4xl">
            {h1}
          </h1>
          <p className="text-steel-light text-lg md:text-xl max-w-3xl leading-relaxed">
            {intro}
          </p>
        </section>

        {/* Body */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8 space-y-8 text-steel-light text-base md:text-lg leading-relaxed">
              {children}
            </article>

            <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start space-y-6">
              <div className="border border-steel-dark bg-bg-secondary p-6">
                <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-3">
                  Talk to Epicom
                </div>
                <p className="text-steel-light text-sm mb-5 leading-relaxed">
                  Discuss your project or request a quote. We work with commercial
                  facilities, farms, acreages and business owners across Southern Alberta.
                </p>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider px-5 py-3 text-sm transition-colors"
                >
                  Request a Quote <ArrowRight size={14} />
                </Link>
              </div>

              {relatedLinks.length > 0 && (
                <div className="border border-steel-dark bg-bg-secondary p-6">
                  <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-3">
                    Related Services
                  </div>
                  <ul className="space-y-2">
                    {relatedLinks.map((r) => (
                      <li key={r.to}>
                        <Link
                          to={r.to}
                          className="text-sm text-white/90 hover:text-signal transition-colors inline-flex items-center gap-1.5"
                        >
                          <ChevronRight size={12} className="text-signal" />
                          {r.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border border-steel-dark bg-bg-primary/60 p-6">
                <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-3">
                  Service Area
                </div>
                <p className="text-sm text-steel-light leading-relaxed">
                  Lethbridge, Cardston, Magrath, Fort Macleod, Pincher Creek, Claresholm,
                  Taber, Medicine Hat and communities across Southern Alberta. We travel
                  regularly for larger commercial and rural projects.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/** Reusable typography blocks so each page reads consistently. */
export function H2({ children }) {
  return (
    <h2 className="heading text-2xl md:text-3xl text-white uppercase leading-tight pt-4 flex items-center gap-3">
      <span className="inline-block w-6 h-[2px] bg-signal" />
      {children}
    </h2>
  );
}
export function H3({ children }) {
  return (
    <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-signal pt-2">
      {children}
    </h3>
  );
}
export function P({ children }) {
  return <p className="leading-relaxed">{children}</p>;
}
export function UL({ children }) {
  return (
    <ul className="space-y-2 pl-0">
      {React.Children.map(children, (c, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="text-signal mt-[10px] w-2 h-[2px] bg-signal inline-block flex-shrink-0" />
          <span>{c}</span>
        </li>
      ))}
    </ul>
  );
}
